import { TProductCategory } from '@lib/core/products/types';
import { IThirdPartyNewsletterTexts, ThirdPartyConnectModalScenario } from '@lib/core/service/types/interface';
import { localeCommon } from '@lib/tools/locale/source/web/common';
import { getRatedProductImage, getSavedPlaceImage, getSavedProductImage } from '@lib/tools/shared/helpers';

import Button from '@components/web/src/atoms/Buttons/Button';
import { CloseButton } from '@components/web/src/atoms/Buttons/CloseButton/CloseButton';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Image } from '@components/web/src/foundations/Image/Image';
import { Text } from '@components/web/src/foundations/Text/Text';
import { COLORS } from '@components/web/src/styles';
import { ModalWrapper } from '@components/web/src/templates/Modals/ModalWrapper/ModalWrapper';
import * as S from '@components/web/src/templates/Modals/ThirdPartyConnectModal/styles';

interface Props {
  retailerLogo: string;
  retailerPrivacyLink: string;
  isOpen: boolean;
  productCategory: TProductCategory;
  productName: string;
  scenario:
    | ThirdPartyConnectModalScenario.LOCATION
    | ThirdPartyConnectModalScenario.RATE5
    | ThirdPartyConnectModalScenario.RATE4
    | ThirdPartyConnectModalScenario.WISHLIST;
  thirdPartyNewsletterTexts: IThirdPartyNewsletterTexts;
  retailerName: string;
  hideModal: () => void;
  handleBtnClick: (acceptance: boolean) => void;
}

export const ThirdPartyConnectModal = ({
  retailerLogo,
  isOpen,
  scenario,
  productCategory,
  productName,
  thirdPartyNewsletterTexts,
  retailerName,
  hideModal,
  handleBtnClick,
  retailerPrivacyLink,
}: Props) => {
  const { productSaveTitle, rateProductTitle, locationTitle } = localeCommon.thirdPartyConnect;
  const { titleText, acceptBtnText, rejectBtnText, descriptionText } = thirdPartyNewsletterTexts || {};
  const { thirdPartyPrivacyUrlText } = localeCommon.thirdPartyPrivacyLink;
  const titleTexts = {
    [ThirdPartyConnectModalScenario.LOCATION]: locationTitle,
    [ThirdPartyConnectModalScenario.WISHLIST]: productSaveTitle,
    [ThirdPartyConnectModalScenario.RATE4]: rateProductTitle,
    [ThirdPartyConnectModalScenario.RATE5]: rateProductTitle,
  };

  const icon = (() => {
    switch (scenario) {
      case ThirdPartyConnectModalScenario.LOCATION:
        return getSavedPlaceImage(productCategory);
      case ThirdPartyConnectModalScenario.RATE4:
        return getRatedProductImage(productCategory, 4);
      case ThirdPartyConnectModalScenario.RATE5:
        return getRatedProductImage(productCategory, 5);
      case ThirdPartyConnectModalScenario.WISHLIST:
        return getSavedProductImage(productCategory);
      default:
        return undefined;
    }
  })();

  return (
    <ModalWrapper isOpen={isOpen} onClose={hideModal}>
      <S.ThirdPartyConnectModalContainer isFullWidth align="center" direction="column" gap={0} padding="16px 16px 24px">
        <CloseButton handleClick={hideModal} position="right" />
        <Flexbox isFullWidth align="center" direction="column" gap={20}>
          {icon && <Image alt="icon" height="89px" objectFit="contain" src={icon} width="83px" />}
          <Flexbox align="center" direction="column" gap={8}>
            {retailerLogo && (
              <Image alt="retailer" height="100px" objectFit="contain" src={retailerLogo} width="250px" />
            )}
            <Text
              fontType="grace"
              localeIndex={{ productName, retailerName }}
              size="h6"
              text={titleTexts[scenario]}
              textAlign="center"
              weight="semibold"
            />
          </Flexbox>
          <Text localeOptions={{ retailerName }} size="body1" text={titleText} />
          <Flexbox isFullWidth direction="column" gap={8}>
            <Button
              isFullWidth
              fontSize="subtitle2"
              handleClick={() => handleBtnClick(true)}
              size="md"
              text={acceptBtnText}
            />
            <Button
              isFullWidth
              handleClick={() => handleBtnClick(false)}
              size="md"
              text={rejectBtnText}
              variant="link"
            />
          </Flexbox>
          <Flexbox direction="column" gap={20}>
            <Text size="body3" text={descriptionText} />
            {!!retailerPrivacyLink && (
              <Text
                color={COLORS.base.black}
                localeOptions={{ retailerName }}
                size="body3"
                text={thirdPartyPrivacyUrlText}
                insertIntlJSXPartOptions={{
                  className: '',
                  linkId: 'third-party-privacy-link',
                  linkRender: text => (
                    <span
                      aria-hidden
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => window.open(retailerPrivacyLink, '_blank')}
                    >
                      <span>{text}</span>
                    </span>
                  ),
                  textId: thirdPartyPrivacyUrlText.id,
                }}
              />
            )}
          </Flexbox>
        </Flexbox>
      </S.ThirdPartyConnectModalContainer>
    </ModalWrapper>
  );
};
